/**
 * RepoVisibilityModal — edits the INTEGRATION-LEVEL master repo allowlist.
 *
 * The OUTER of the two-level repo model:
 *   1. Integration level (THIS modal) = the master allowlist: which repos are
 *      VISIBLE to ALL projects on the account. Options = every repo the
 *      connected token/App can see (GET /:provider/repos); the checked set is
 *      the saved master (GET /:provider/status → selectedRepos). Save writes it
 *      via POST /:provider/selection. Editing it does NOT touch any project's
 *      selection.
 *   2. Project level (Project Settings → Repository Access) = a per-project
 *      SUBSET of this master.
 *
 * An EMPTY master means "all repositories are available" (backend gating is
 * opt-in / backward compatible) — projects then see the full token list.
 *
 * Provider-symmetric: `provider` is 'github' | 'gitlab'. The master is a flat
 * list of full-path strings (one field, one type); both providers save the
 * same shape and the backend lowercases + dedupes.
 *
 * ONE CONNECTION AT A TIME. An account can hold SEVERAL GitLab connections (a
 * self-managed instance AND gitlab.com), each with its OWN master, and the
 * Integrations page draws a card per connection — so this modal is always
 * editing exactly one of them and must say WHICH:
 *   - options  ← GET /gitlab/repos?integrationId=…  (that instance's repos)
 *   - checked  ← status.instances[…].selectedRepos  (that instance's master)
 *   - save     → POST /gitlab/selection { repos, integrationId }
 * Reading `status.selectedRepos` here would be the UNION across instances, and
 * saving without the id lands on whichever row the backend picks as newest —
 * which is how the gitlab.com row acquired a repo only the self-managed
 * instance hosts (fixed backend-side in bfdfbc55; this is the cloud UI half).
 * `integrationId` is optional: GitHub is account-wide and passes none, which
 * keeps its behaviour byte-identical.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import api from '../../services/api';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function RepoVisibilityModal({ isOpen, onClose, provider, integrationId, instanceLabel }) {
  const provName = provider === 'gitlab' ? 'GitLab' : 'GitHub';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [repos, setRepos] = useState([]); // options: [{ full_name, private, instanceHost }]
  const [selected, setSelected] = useState({}); // { lowerFullName: true }
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const q = integrationId ? `?integrationId=${encodeURIComponent(integrationId)}` : '';
      const [status, repoResp] = await Promise.all([
        api.get(`/${provider}/status`).catch(() => ({})),
        api.get(`/${provider}/repos${q}`).catch((e) => ({ _err: e })),
      ]);
      // THIS connection's master, never the union. An older backend that
      // predates `instances` has exactly one connection, so its flat
      // `selectedRepos` IS this instance's list — that fallback cannot mix two.
      const inst = integrationId && Array.isArray(status?.instances)
        ? status.instances.find((i) => i && i.integrationId === integrationId)
        : null;
      const master = inst
        ? (Array.isArray(inst.selectedRepos) ? inst.selectedRepos : [])
        : (integrationId && Array.isArray(status?.instances)
          // The id names a connection this account no longer has: show an empty
          // master rather than another instance's, and let the save 404.
          ? []
          : (Array.isArray(status?.selectedRepos) ? status.selectedRepos : []));
      const sel = {};
      master.forEach((nm) => { if (nm) sel[String(nm).toLowerCase()] = true; });

      let options = [];
      if (repoResp && !repoResp._err) options = repoResp.repos || [];
      // Surface any master entry the token can't currently see so it stays
      // visible + checkable instead of being silently dropped on the next save.
      const known = new Set(options.map((r) => String(r.full_name || '').toLowerCase()));
      master.forEach((nm) => {
        const key = String(nm || '').toLowerCase();
        if (nm && !known.has(key)) options.push({ full_name: nm, _missing: true });
      });

      setRepos(options);
      setSelected(sel);
      if (repoResp && repoResp._err && options.length === 0) {
        setError(`Couldn't load ${provName} repositories — connect ${provName} first, then retry.`);
      }
    } finally {
      setLoading(false);
    }
  }, [provider, provName, integrationId]);

  useEffect(() => {
    if (isOpen) {
      setFilter('');
      load();
    }
  }, [isOpen, load]);

  const toggle = (fullName) => {
    const key = String(fullName || '').toLowerCase();
    setSelected((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key]; else next[key] = true;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    // Send the ORIGINAL-case full_names of the checked repos; the backend
    // lowercases + dedupes into the stored master.
    const chosen = repos
      .filter((r) => selected[String(r.full_name || '').toLowerCase()])
      .map((r) => r.full_name);
    try {
      // Name the row being written. Without it the backend falls back to
      // "the account's newest gitlab row", i.e. the cross-instance write.
      await api.post(`/${provider}/selection`, integrationId ? { repos: chosen, integrationId } : { repos: chosen });
      onClose?.(true);
    } catch (e) {
      setSaving(false);
      setError(e?.message || 'Save failed.');
    }
  };

  const q = filter.trim().toLowerCase();
  const visible = q ? repos.filter((r) => String(r.full_name || '').toLowerCase().includes(q)) : repos;
  const selectedCount = Object.keys(selected).length;

  return (
    <IonModal isOpen={isOpen} onDidDismiss={() => onClose?.(false)} className="gitlab-connect-modal">
      <div className="gitlab-modal-content" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <h2>Visible repositories · {provName}{instanceLabel ? ` · ${instanceLabel}` : ''}</h2>
        <p className="gitlab-modal-description">
          Choose which {instanceLabel ? `${instanceLabel} ` : ''}{provName} repositories are visible to Zibby. Every project can then pick from THIS list which repos its
          agents use (Project Settings → Repository access). Leave everything unchecked to make all repositories available.
        </p>

        {loading ? (
          <div style={{ padding: '24px 0', textAlign: 'center' }}><IonSpinner name="crescent" /></div>
        ) : (
          <>
            {repos.length > 8 && (
              <div className="gitlab-form-group">
                <input
                  type="text"
                  className="gitlab-form-input"
                  placeholder={`Filter ${provName} repositories…`}
                  value={filter}
                  onChange={(e) => setFilter(e.target.value || '')}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
            )}
            <div style={{ maxHeight: '320px', overflow: 'auto', border: '1px solid var(--ion-color-step-150, #e0e0e0)', borderRadius: '8px', padding: '2px 10px' }}>
              {visible.length === 0 ? (
                <p style={{ padding: '12px 4px', opacity: 0.7 }}>No repositories found.</p>
              ) : visible.map((r) => {
                const key = String(r.full_name || '').toLowerCase();
                return (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 4px', borderBottom: '1px solid var(--ion-color-step-100, #f0f0f0)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!selected[key]} onChange={() => toggle(r.full_name)} />
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.full_name}
                      {r._missing && <span style={{ fontSize: '11px', opacity: 0.6 }}> (allowed — not visible to current token; uncheck to remove)</span>}
                    </span>
                    {r.instanceHost && <span style={{ fontSize: '11px', opacity: 0.6, flex: '0 0 auto' }}>{r.instanceHost}</span>}
                    {r.private && <span className="repo-badge" style={{ flex: '0 0 auto' }}>Private</span>}
                  </label>
                );
              })}
            </div>
          </>
        )}

        {error && <p className="gitlab-modal-error">{error}</p>}

        <div className="gitlab-modal-buttons">
          <IonButton expand="block" onClick={handleSave} disabled={saving || loading}>
            {saving ? <IonSpinner name="crescent" /> : `Save (${selectedCount} selected)`}
          </IonButton>
          <IonButton expand="block" fill="clear" onClick={() => onClose?.(false)} disabled={saving}>Cancel</IonButton>
        </div>
      </div>
    </IonModal>
  );
}
